import React, { useState } from 'react';

interface SMSAuthProps {
  onAuthenticated: (token: string, phone: string) => void;
}

export const SMSAuth: React.FC<SMSAuthProps> = ({ onAuthenticated }) => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const apiBase = import.meta.env.VITE_API_BASE || 'https://8kgetter.jon-13b.workers.dev';

  const requestCode = async () => {
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    // Format US phone number (no country code required)
    let formattedPhone = phone.trim().replace(/\D/g, '');
    
    // Add +1 prefix for US numbers (10 digits)
    if (formattedPhone.length === 10) {
      formattedPhone = '+1' + formattedPhone;
    } else if (formattedPhone.length === 11 && formattedPhone.startsWith('1')) {
      formattedPhone = '+' + formattedPhone;
    } else if (!formattedPhone.startsWith('+')) {
      setError('Please enter a valid US phone number (10 digits)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('=== FRONTEND DEBUG ===');
      console.log('API Base:', apiBase);
      console.log('Formatted Phone:', formattedPhone);
      console.log('Request URL:', `${apiBase}/api/auth/request-code`);
      
      const response = await fetch(`${apiBase}/api/auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formattedPhone })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        setPhone(formattedPhone);
        setStep('code');
      } else {
        setError(data.error || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('Request error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBase}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code: code.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        onAuthenticated(data.token, phone);
      } else {
        setError(data.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (step === 'phone') {
        requestCode();
      } else {
        verifyCode();
      }
    }
  };

  const styles = {
    container: {
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    card: {
      background: 'white',
      borderRadius: '16px',
      padding: '32px',
      width: '100%',
      maxWidth: '400px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
      textAlign: 'center' as const
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '32px'
    },
    input: {
      width: '100%',
      padding: '16px',
      fontSize: '16px',
      border: '2px solid #e1e5e9',
      borderRadius: '12px',
      marginBottom: '16px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box' as const
    },
    button: {
      width: '100%',
      padding: '16px',
      fontSize: '16px',
      fontWeight: '600',
      color: 'white',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'transform 0.2s',
      marginBottom: '16px'
    },
    backButton: {
      background: 'transparent',
      color: '#667eea',
      border: '2px solid #667eea',
      marginBottom: '16px'
    },
    error: {
      color: '#e74c3c',
      fontSize: '14px',
      marginBottom: '16px',
      padding: '12px',
      background: '#ffeaea',
      borderRadius: '8px'
    },
    info: {
      color: '#666',
      fontSize: '14px',
      lineHeight: '1.4'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>8kgetter.com</h1>
        <p style={styles.subtitle}>
          {step === 'phone' 
            ? 'Get 8-Ks and more, right to your phone' 
            : 'Enter the verification code sent to your phone'
          }
        </p>

        {error && <div style={styles.error}>{error}</div>}

        {step === 'phone' ? (
          <>
            <input
              type="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyPress={handleKeyPress}
              style={styles.input}
              disabled={loading}
            />
            <button
              onClick={requestCode}
              disabled={loading}
              style={styles.button}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
            <p style={styles.info}>
              We'll send you a 6-digit code to verify your US phone number
              <br /><br />
              By continuing, you agree to our{" "}
              <a 
                href="https://8kgetter.com/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: "#667eea", textDecoration: "underline" }}
              >
                Terms and Conditions
              </a>
              {" "}and{" "}
              <a 
                href="https://8kgetter.com/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: "#667eea", textDecoration: "underline" }}
              >
                Privacy Policy
              </a>.
            </p>
          </>
        ) : (
          <>
            <input
              type="text"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              style={styles.input}
              disabled={loading}
              maxLength={6}
            />
            <button
              onClick={verifyCode}
              disabled={loading}
              style={styles.button}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              onClick={() => {
                setStep('phone');
                setCode('');
                setError('');
              }}
              style={{...styles.button, ...styles.backButton}}
            >
              Back to Phone Number
            </button>
            <p style={styles.info}>
              Code sent to {phone}. Check your messages and enter the 6-digit code above.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
